import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Sparkles, Calendar, Users, Smartphone, MessageCircle, ShieldCheck,
  ArrowRight, Check, ChevronDown, Wand2, Star, Heart, Menu, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { detectCampaign } from "@/lib/campaigns";

// ============== ヘルパー ==============
const Section = ({ id, className = "", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${className}`}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

// ============== ナビ ==============
function Navbar() {
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#features", label: "特長" },
    { href: "#magic", label: "配置マジック" },
    { href: "#proxy-ai", label: "代理入力×AI" },
    { href: "#showcase", label: "画面プレビュー" },
    { href: "#pricing", label: "料金" },
    { href: "#faq", label: "よくある質問" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-stone-100" data-testid="lp-navbar">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="lp-logo">
          <img src="/yasamaru-celebrate-128.png" alt="" className="w-9 h-9" />
          <span className="font-heading font-bold text-lg text-stone-800 tracking-tight">やさまるシフト</span>
        </a>
        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-sm text-stone-600 hover:text-emerald-600 transition-colors font-medium">
              {l.label}
            </a>
          ))}
          <Link to="/login" data-testid="lp-nav-login"
            className="text-sm text-stone-600 hover:text-emerald-600 font-medium">
            ご利用中の方はこちら
          </Link>
          <a href="#contact" data-testid="lp-nav-cta"
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm px-5 py-2.5 rounded-full shadow-[0_4px_14px_0_rgba(16,185,129,0.39)] transition-all hover:-translate-y-0.5">
            無料トライアル
          </a>
        </nav>
        <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="メニュー" data-testid="lp-menu-toggle">
          {open ? <X className="w-5 h-5 text-stone-700" /> : <Menu className="w-5 h-5 text-stone-700" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-white/95 border-t border-stone-100"
          >
            <div className="px-4 py-3 flex flex-col gap-3">
              {links.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-stone-700 py-1.5 text-sm font-medium">{l.label}</a>
              ))}
              <Link to="/login" onClick={() => setOpen(false)} className="text-stone-700 py-1.5 text-sm font-medium">ご利用中の方はこちら</Link>
              <a href="#contact" onClick={() => setOpen(false)} className="bg-emerald-500 text-white font-bold py-2.5 rounded-full text-center text-sm">無料トライアル</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ============== ヒーロー ==============
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative pt-12 pb-20 lg:pt-20 lg:pb-32 overflow-hidden"
      style={{
        background:
          "radial-gradient(80% 60% at 75% 30%, #FFF7E0 0%, transparent 60%), radial-gradient(70% 60% at 15% 60%, #E6F4EB 0%, transparent 60%), #FDFBF7",
      }}
      data-testid="lp-hero"
    >
      {/* 漂う粒子 */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {[...Array(14)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-amber-300/60"
            style={{
              width: 6 + (i % 4) * 3, height: 6 + (i % 4) * 3,
              top: `${(i * 7 + 8) % 90}%`, left: `${(i * 11 + 5) % 95}%`,
            }}
            animate={{ y: [0, -22, 0], opacity: [0.4, 0.9, 0.4] }}
            transition={{ duration: 4 + (i % 4), repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
          />
        ))}
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-xs font-bold mb-5">
              <Sparkles className="w-3.5 h-3.5" />AIが、シフト作成を魔法に変える
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-[1.15] text-stone-800 tracking-tight">
              やさしく、まるく、<br />
              <span className="relative inline-block">
                シフトに
                <span className="relative text-emerald-600">
                  マジック
                  <svg className="absolute -bottom-1 left-0 w-full" height="10" viewBox="0 0 200 10" fill="none">
                    <path d="M2 7 Q 50 1 100 4 T 198 5" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </span>。
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 text-stone-600 text-base md:text-lg leading-relaxed max-w-xl">
              希望日数の集約から仮配置まで、AI「やさまる」が魔法のように一瞬で。<br className="hidden md:inline" />
              店長さんもスタッフさんも、もう月末に頭を抱えない。
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-7 py-3.5 rounded-full text-base shadow-[0_8px_24px_-6px_rgba(16,185,129,0.45)] transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
              >
                まずは無料トライアルに申し込む<ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/login"
                data-testid="hero-cta-secondary"
                className="bg-white text-emerald-700 border-2 border-emerald-100 hover:bg-emerald-50 font-bold px-7 py-3.5 rounded-full text-base transition-all"
              >
                既にお使いの方はこちら
              </Link>
            </motion.div>
            <motion.p variants={fadeUp} className="mt-4 text-xs text-stone-500">
              ✓ 初期費用¥0　 ✓ 無料トライアル2ヶ月　 ✓ クレジットカード不要
            </motion.p>
          </motion.div>

          {/* 右側：やさまる + 浮遊カード */}
          <motion.div style={{ y }} className="relative h-[380px] md:h-[440px]">
            <FloatingMagicScene />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============== モニターキャンペーン バナー ==============
function MonitorCampaign({ campaign }) {
  if (!campaign) return null;
  const headline = campaign.headline || ["いま申し込むと、", "最大 4ヶ月もお得に始められます。"];
  const badge = campaign.badge || "先着 30 社 限定モニター募集中";
  const deadline = campaign.deadlineLabel || "2026年6月5日";
  return (
    <section
      id="monitor"
      className="relative py-12 lg:py-16"
      style={{
        background:
          "linear-gradient(135deg, #ECFDF5 0%, #FEF3C7 100%)",
      }}
      data-testid="lp-monitor-campaign"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-white/85 backdrop-blur border-2 border-dashed border-emerald-400 shadow-[0_18px_40px_-12px_rgba(16,185,129,0.25)] p-6 lg:p-10">
          <div className="text-center max-w-3xl mx-auto">
            {campaign.displayName && (
              <p className="text-[11px] font-bold text-rose-700 tracking-widest mb-1" data-testid="campaign-target">
                {campaign.displayName}
              </p>
            )}
            <div className="inline-flex items-center gap-1.5 bg-rose-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
              <Sparkles className="w-3.5 h-3.5" />
              {badge}
            </div>
            <h2 className="font-heading font-black text-2xl md:text-3xl lg:text-4xl mt-3 text-stone-900 leading-tight tracking-tight">
              {headline[0]}<br className="lg:hidden" />
              <span className="text-emerald-600">{headline[1]}</span>
            </h2>
            <p className="mt-3 text-sm text-stone-600 leading-relaxed">
              応募締切：<b className="text-rose-700">{deadline}</b>　/　常時 3名以上のスタッフがいる店舗が対象目安
            </p>
          </div>

          <div className="mt-6 lg:mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-center">
              <div className="text-[10px] font-bold text-emerald-700 tracking-widest">特典 1</div>
              <div className="text-3xl lg:text-4xl font-black text-emerald-700 mt-1">2ヶ月</div>
              <div className="text-sm font-bold text-stone-800 mt-1">無料お試し</div>
              <p className="text-[11px] text-stone-500 mt-1.5 leading-relaxed">
                登録から最初の2ヶ月は完全無料
              </p>
            </div>
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 text-center">
              <div className="text-[10px] font-bold text-amber-700 tracking-widest">特典 2</div>
              <div className="text-3xl lg:text-4xl font-black text-amber-700 mt-1">50% OFF</div>
              <div className="text-sm font-bold text-stone-800 mt-1">さらに 2ヶ月</div>
              <p className="text-[11px] text-stone-500 mt-1.5 leading-relaxed">
                3〜4ヶ月目は通常料金の半額
              </p>
            </div>
            <div className="rounded-2xl bg-blue-50 border border-blue-200 p-5 text-center">
              <div className="text-[10px] font-bold text-blue-700 tracking-widest">特典 3</div>
              <div className="text-3xl lg:text-4xl font-black text-blue-700 mt-1">無料</div>
              <div className="text-sm font-bold text-stone-800 mt-1">初期設定サポート</div>
              <p className="text-[11px] text-stone-500 mt-1.5 leading-relaxed">
                導入時の設定をスタッフが伴走
              </p>
            </div>
          </div>

          <div className="mt-6 lg:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#contact"
              data-testid="monitor-cta"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-7 py-3.5 rounded-full text-base shadow-[0_8px_24px_-6px_rgba(16,185,129,0.45)] transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              モニターに申し込む<ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/flyer"
              data-testid="monitor-flyer-link"
              target="_blank"
              className="bg-white text-emerald-700 border-2 border-emerald-200 hover:bg-emerald-50 font-bold px-7 py-3.5 rounded-full text-base transition-all"
            >
              チラシを印刷する（A4両面）
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FloatingMagicScene() {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* 後光 */}
      <div className="absolute w-72 h-72 md:w-80 md:h-80 bg-gradient-radial from-amber-200/60 via-amber-100/30 to-transparent rounded-full blur-3xl" />
      <motion.img
        src="/yasamaru-cast-256.png" alt="やさまる"
        className="relative w-60 md:w-72 drop-shadow-[0_20px_30px_rgba(16,185,129,0.18)]"
        animate={{ y: [0, -14, 0] }}
        transition={{ repeat: Infinity, duration: 3.6, ease: "easeInOut" }}
      />
      {/* 浮遊カード: シフトブロック */}
      <motion.div
        initial={{ opacity: 0, x: -30, y: -10 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
        className="absolute top-6 left-2 md:left-0 bg-white rounded-2xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.12)] border border-stone-100 px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">佐</div>
          <div>
            <p className="text-xs font-bold text-stone-800">佐藤さん</p>
            <p className="text-[10px] text-stone-500">9:00 〜 17:00</p>
          </div>
          <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">配置</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 30, y: 20 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
        className="absolute top-32 right-0 bg-white rounded-2xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.12)] border border-stone-100 px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm">田</div>
          <div>
            <p className="text-xs font-bold text-stone-800">田中さん</p>
            <p className="text-[10px] text-stone-500">13:00 〜 21:00</p>
          </div>
          <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">候補</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.6 }}
        className="absolute bottom-6 left-6 md:left-2 bg-white rounded-2xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.12)] border border-stone-100 px-4 py-3 flex items-center gap-2"
      >
        <Sparkles className="w-4 h-4 text-purple-500" />
        <p className="text-xs font-bold text-stone-700">配置マジック実行中…</p>
      </motion.div>
    </div>
  );
}

// ============== 痛みポイント ==============
function PainPoints() {
  const items = [
    { icon: Calendar, title: "毎月のシフト作成に\n何時間も…", desc: "店長業務の合間を縫って、Excel と LINE のやり取りに半日。" },
    { icon: MessageCircle, title: "希望が紙・LINE・口頭で\nバラバラ…", desc: "「あれ、誰が休みだっけ？」 集約だけで疲れてしまう。" },
    { icon: Users, title: "「入れる人」と「必要な人数」\nのすれ違い…", desc: "気付いたら、人手不足の日と過剰な日が混在している。" },
  ];
  return (
    <Section id="pain" className="bg-[#F8F4EC]/40">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center mb-12 md:mb-16">
        <motion.h2 variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
          こんなお悩み、<br className="md:hidden" />ありませんか？
        </motion.h2>
        <motion.p variants={fadeUp} className="mt-3 text-stone-500 text-sm md:text-base">店長さん、毎月本当におつかれさまです…</motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {items.map((it, idx) => (
          <motion.div
            key={idx} variants={fadeUp}
            whileHover={{ y: -4, scale: 1.01 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="bg-white rounded-3xl p-7 border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            data-testid={`pain-card-${idx}`}
          >
            <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
              <it.icon className="w-5 h-5 text-amber-700" />
            </div>
            <h3 className="font-heading font-bold text-lg text-stone-800 whitespace-pre-line leading-snug">{it.title}</h3>
            <p className="text-sm text-stone-500 mt-2 leading-relaxed">{it.desc}</p>
          </motion.div>
        ))}
      </motion.div>

      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }} className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4">
        <img src="/yasamaru-shy-128.png" alt="" className="w-20 h-20" />
        <div className="relative bg-white border border-stone-100 rounded-2xl px-5 py-3 shadow-[0_6px_20px_-6px_rgba(0,0,0,0.08)] max-w-md">
          <p className="text-sm text-stone-700 leading-relaxed">店長さん、やさまるがお手伝いします。<br className="md:hidden" />シフトの魔法、見せちゃいます…！</p>
          <span className="hidden md:block absolute -left-2 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-l border-b border-stone-100 rotate-45" />
        </div>
      </motion.div>
    </Section>
  );
}

// ============== 配置マジックデモ (実画面動画版) ==============
function MagicDemo() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  // セクションに入ったら自動再生
  useEffect(() => {
    if (!sectionRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && videoRef.current && !started) {
            videoRef.current.play().then(() => {
              setPlaying(true);
              setStarted(true);
            }).catch(() => {
              // autoplay blocked, fine — user can click
            });
          }
        }
      },
      { threshold: 0.45 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [started]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play();
    setPlaying(true);
    setStarted(true);
  };

  return (
    <Section id="magic" className="bg-white">
      <div ref={sectionRef} className="text-center mb-10 md:mb-14">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
            <Wand2 className="w-3.5 h-3.5" />配置マジック
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
            AI が、5秒で<br className="md:hidden" />ベスト配置を魔法のように。
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-3 text-stone-500 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
            スタッフの希望日数・週上限・連勤の回避を一気に考慮して、AIが仮シフトを自動生成。<br className="hidden md:inline" />
            あとは画面でちょこっと微調整するだけ。
          </motion.p>
          <motion.p variants={fadeUp} className="mt-2 text-[11px] text-rose-500 font-bold">
            ↓ 実際のアプリ画面の動画です ↓
          </motion.p>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7 }}
        className="relative max-w-5xl mx-auto"
      >
        {/* 後光 */}
        <div className="absolute -inset-8 bg-gradient-to-br from-amber-200/40 via-purple-200/30 to-emerald-200/40 rounded-[3rem] blur-3xl -z-0" />

        <div className="relative rounded-[1.7rem] bg-stone-900 p-1 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.3)]"
          style={{ background: "linear-gradient(135deg, #34D399, #FCD34D, #C084FC, #34D399)", backgroundSize: "300% 300%", animation: "lp-magic-flow 6s ease infinite" }}
        >
          <div className="bg-stone-50 rounded-[1.6rem] overflow-hidden">
            {/* ブラウザバー */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-stone-100 border-b border-stone-200">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
              <span className="ml-3 text-[11px] text-stone-500 font-mono truncate">yasamaru-shift.app / シフト作成 / 配置マジック</span>
            </div>

            {/* 動画 */}
            <div className="relative bg-black aspect-[16/9]">
              <video
                ref={videoRef}
                className="w-full h-full block object-cover"
                poster="/lp-magic-poster.jpg"
                muted
                loop
                playsInline
                preload="metadata"
                data-testid="lp-magic-video"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
              >
                <source src="/lp-magic.mp4" type="video/mp4" />
                <source src="/lp-magic.webm" type="video/webm" />
              </video>

              {/* やさまるオーバーレイ (再生前のみ) */}
              {!started && (
                <button onClick={replay} data-testid="lp-magic-play"
                  className="absolute inset-0 group flex flex-col items-center justify-center bg-stone-900/55 backdrop-blur-[2px] hover:bg-stone-900/65 transition-colors">
                  <motion.img src="/yasamaru-cast-256.png" alt="" className="w-24 md:w-32 drop-shadow-2xl"
                    animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2.6 }}
                  />
                  <div className="mt-3 inline-flex items-center gap-2 bg-white/95 hover:bg-white text-stone-800 font-bold px-5 py-2.5 rounded-full text-sm md:text-base shadow-lg group-hover:scale-105 transition-transform">
                    <Wand2 className="w-4 h-4 text-purple-600" />配置マジックを見る (約12秒)
                  </div>
                </button>
              )}

              {/* 再生中の小さなコントロール */}
              {started && (
                <div className="absolute bottom-3 right-3 flex gap-2">
                  <button onClick={togglePlay} aria-label={playing ? "一時停止" : "再生"}
                    className="bg-stone-900/70 hover:bg-stone-900/85 text-white rounded-full w-9 h-9 inline-flex items-center justify-center transition-colors backdrop-blur-sm"
                    data-testid="lp-magic-toggle"
                  >
                    {playing ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5z"/></svg>
                    )}
                  </button>
                  <button onClick={replay} aria-label="最初から再生"
                    className="bg-stone-900/70 hover:bg-stone-900/85 text-white rounded-full px-3 h-9 inline-flex items-center justify-center text-xs font-bold transition-colors backdrop-blur-sm gap-1"
                    data-testid="lp-magic-replay"
                  >
                    <Sparkles className="w-3 h-3" />もう一度
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 補足キャプション */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs md:text-sm text-stone-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-purple-400" />配置マジックを起動</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" />AIが5秒で配置</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" />カレンダーで確認</span>
        </div>
      </motion.div>
    </Section>
  );
}

// ============== 実際のアプリ画面プレビュー ==============
function Showcase() {
  return (
    <Section id="showcase" className="bg-[#F8F4EC]/40">
      <div className="text-center mb-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="inline-flex items-center gap-1.5 bg-sky-100 text-sky-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
          <Smartphone className="w-3.5 h-3.5" />実際の画面プレビュー
        </motion.div>
        <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
          こんな画面で、<br className="md:hidden" />シフト作成が変わります。
        </motion.h2>
        <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.1 }} className="mt-3 text-stone-500 max-w-xl mx-auto text-sm md:text-base">
          管理者画面もスタッフ画面も、迷わず使えるように設計しました。
        </motion.p>
      </div>

      {/* 大きい画面 + 小さい画面のベントレイアウト */}
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger}
        className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 mb-5">
        <ShowcaseCard
          variant="large"
          img="/lp-screen-3-adjust.jpg"
          tag="管理者画面"
          tagTone="emerald"
          title="日を調整する"
          desc="配置マジックでベース配置がすでに入った状態。日付をクリックすると右側にその日の出勤メンバーと候補スタッフが一覧表示され、足りない/代えたい部分だけサッと調整。"
          testId="showcase-adjust"
        />
        <ShowcaseCard
          variant="default"
          img="/lp-screen-1-prep.jpg"
          tag="管理者画面"
          tagTone="amber"
          title="提出状況がひと目で"
          desc="誰がもう提出して、誰がまだか。未提出者には1クリックでリマインドも送れます。"
          testId="showcase-prep"
        />
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger}
        className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <ShowcaseCard
          variant="default"
          img="/lp-screen-4-confirmed.jpg"
          tag="管理者画面"
          tagTone="sky"
          title="確定シフトを一覧で"
          desc="月のシフトをひと目で確認、月をまたいでの編集も自在に。"
          testId="showcase-confirmed"
        />
        <ShowcaseCard
          variant="mobile"
          img="/lp-screen-5-request.jpg"
          tag="スタッフ画面"
          tagTone="rose"
          title="スマホから希望提出"
          desc="◎入れます / △必要なら / ×入れません を、スマホでタップするだけ。"
          testId="showcase-staff-request"
        />
        <ShowcaseCard
          variant="mobile"
          img="/lp-screen-5-mobile.jpg"
          tag="スタッフ画面"
          tagTone="purple"
          title="ホームでひと目で確認"
          desc="自分のシフト・お知らせメッセージを、スマホのホーム画面のように。"
          testId="showcase-staff-home"
        />
      </motion.div>
    </Section>
  );
}

const TAG_TONES = {
  emerald: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  sky: "bg-sky-100 text-sky-700",
  rose: "bg-rose-100 text-rose-700",
  purple: "bg-purple-100 text-purple-700",
};

function ShowcaseCard({ variant, img, tag, tagTone, title, desc, testId }) {
  const isMobile = variant === "mobile";
  return (
    <motion.div variants={fadeUp}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className="group bg-white rounded-3xl p-5 md:p-6 border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] transition-all"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${TAG_TONES[tagTone]}`}>{tag}</span>
      </div>
      <h3 className="font-heading font-bold text-lg text-stone-800">{title}</h3>
      <p className="text-xs md:text-sm text-stone-500 mt-1.5 mb-4 leading-relaxed">{desc}</p>
      <div className={`relative ${isMobile ? "flex justify-center" : ""}`}>
        {isMobile ? (
          // モバイルモックフレーム
          <div className="relative rounded-[2rem] border-[6px] border-stone-800 bg-stone-800 shadow-[0_12px_30px_-8px_rgba(0,0,0,0.3)] overflow-hidden w-[170px] md:w-[180px]">
            <span className="absolute top-1.5 left-1/2 -translate-x-1/2 w-12 h-1 bg-stone-700 rounded-full z-10" />
            <img src={img} alt={title} className="w-full block" loading="lazy" />
          </div>
        ) : (
          // デスクトップモックフレーム
          <div className="rounded-xl overflow-hidden border border-stone-200 shadow-[0_8px_24px_-6px_rgba(0,0,0,0.12)] bg-stone-50">
            {/* ブラウザバー */}
            <div className="flex items-center gap-1 px-3 py-2 bg-stone-100 border-b border-stone-200">
              <span className="w-2 h-2 rounded-full bg-rose-300" />
              <span className="w-2 h-2 rounded-full bg-amber-300" />
              <span className="w-2 h-2 rounded-full bg-emerald-300" />
              <span className="ml-2 text-[9px] text-stone-400 font-mono truncate">yasamaru-shift.app</span>
            </div>
            <img src={img} alt={title} className="w-full block group-hover:scale-[1.01] transition-transform duration-500" loading="lazy" />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============== 代理入力 + AI解析 セクション ==============
function ProxyAIFeature() {
  const [view, setView] = useState("before"); // before | after
  const ref = useRef(null);

  // 入って4秒後に "after" に切り替えてループ
  useEffect(() => {
    if (!ref.current) return;
    let timer = null;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !timer) {
            const loop = () => {
              setView("before");
              setTimeout(() => setView("after"), 4500);
              timer = setTimeout(loop, 9500);
            };
            loop();
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => { obs.disconnect(); if (timer) clearTimeout(timer); };
  }, []);

  return (
    <Section id="proxy-ai" className="bg-gradient-to-b from-white to-purple-50/30">
      <div ref={ref} className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
        {/* 左: 説明 */}
        <div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
              <Sparkles className="w-3.5 h-3.5" />代理入力 × AIサポート
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight leading-tight">
              「LINEで送ってきた人」も、<br className="hidden md:inline" />
              <span className="relative text-purple-600">
                AIに丸投げで完了。
                <svg className="absolute -bottom-1 left-0 w-full" height="10" viewBox="0 0 200 10" fill="none">
                  <path d="M2 7 Q 50 1 100 4 T 198 5" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-6 text-stone-600 leading-relaxed">
              なかなかログインしてくれないスタッフ、いますよね。<br />
              そんな時は <b className="text-purple-700">店長が代理入力</b>。<br />
              LINEや口頭でもらったメモを <b className="text-purple-700">そのまま貼り付けるだけ</b>で、AIが日付と「入れる/入れない」を理解してカレンダーに自動反映します。
            </motion.p>

            <motion.ul variants={stagger} className="mt-6 space-y-2.5">
              {[
                { text: "「8/3,5,10は入れます。土日休み」のような自然文をそのまま解析", bg: "bg-purple-100", fg: "text-purple-700" },
                { text: "対象月の曜日まで理解。「土日休み」を実際の月に展開", bg: "bg-amber-100", fg: "text-amber-700" },
                { text: "既存の希望は上書き、カレンダーで微調整も可能", bg: "bg-emerald-100", fg: "text-emerald-700" },
                { text: "AI解析しなくても、ワンタップで「全日◎」「土日×」など一括設定", bg: "bg-sky-100", fg: "text-sky-700" },
              ].map((it) => (
                <motion.li key={it.text} variants={fadeUp} className="flex items-start gap-2.5 text-sm text-stone-700">
                  <span className={`mt-0.5 inline-flex w-5 h-5 rounded-full ${it.bg} items-center justify-center shrink-0`}>
                    <Check className={`w-3 h-3 ${it.fg}`} />
                  </span>
                  <span>{it.text}</span>
                </motion.li>
              ))}
            </motion.ul>

            <motion.div variants={fadeUp} className="mt-7 bg-white border border-stone-100 rounded-2xl p-4 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.1)]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-[10px] font-bold">渡</div>
                <p className="text-[11px] text-stone-400 font-bold">渡辺大輝さん - LINE</p>
              </div>
              <LineTyper
                lines={[
                  "8/3、5、10、15は入れます！",
                  "土日はお休みでお願いします。",
                  "8/20-22は家族旅行のためNG。",
                  "それ以外の平日は時間があれば入れます",
                ]}
              />
              <div className="mt-3 flex items-center gap-2 text-xs">
                <ArrowRight className="w-3.5 h-3.5 text-purple-600" />
                <span className="text-purple-700 font-bold">そのまま貼って「解析する」</span>
                <span className="text-stone-500">→ ◎4日・×9日に自動展開</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* 右: スクショの Before/After 切り替え */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative">
          {/* 後光 */}
          <div className="absolute -inset-6 bg-gradient-to-br from-purple-200/40 via-amber-200/30 to-emerald-200/40 rounded-[3rem] blur-3xl -z-0" />
          <div className="relative rounded-[1.5rem] p-1 shadow-[0_20px_60px_-20px_rgba(168,85,247,0.35)]"
            style={{ background: "linear-gradient(135deg, #C084FC, #FCD34D, #34D399, #C084FC)", backgroundSize: "300% 300%", animation: "lp-magic-flow 6s ease infinite" }}>
            <div className="bg-white rounded-[1.4rem] overflow-hidden">
              {/* ブラウザバー */}
              <div className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 border-b border-stone-200">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                <span className="ml-3 text-[10px] text-stone-500 font-mono truncate">代理入力 / AI解析</span>
                <span className="ml-auto text-[10px] font-bold text-purple-600 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />{view === "before" ? "解析前" : "解析後"}
                </span>
              </div>
              <div className="relative bg-stone-50">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={view}
                    initial={{ opacity: 0, x: view === "after" ? 30 : -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: view === "after" ? -30 : 30 }}
                    transition={{ duration: 0.5 }}
                    src={view === "before" ? "/lp-screen-6-proxy-ai-input.jpg" : "/lp-screen-6-proxy-ai-result.jpg"}
                    alt={view === "before" ? "AI解析前" : "AI解析後"}
                    className="w-full block"
                  />
                </AnimatePresence>
              </div>
            </div>
          </div>
          {/* 切替ボタン */}
          <div className="mt-4 flex justify-center gap-2">
            <button onClick={() => setView("before")} data-testid="proxy-ai-tab-before"
              className={`text-xs font-bold px-4 py-1.5 rounded-full border transition-all ${
                view === "before" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
              }`}>
              ① 貼り付け前
            </button>
            <button onClick={() => setView("after")} data-testid="proxy-ai-tab-after"
              className={`text-xs font-bold px-4 py-1.5 rounded-full border transition-all ${
                view === "after" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
              }`}>
              ② AI解析後
            </button>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

// LINE風タイピングアニメーション (吹き出しごとに1文字ずつ表示)
function LineTyper({ lines, charDelay = 55, lineGap = 480, restGap = 2400 }) {
  const [lineIdx, setLineIdx] = useState(0);
  const [chars, setChars] = useState(0);
  const containerRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [version, setVersion] = useState(0); // ループ再開用

  // 入ったときだけ開始
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started) setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [started]);

  // タイピング進行
  useEffect(() => {
    if (!started) return;
    const current = lines[lineIdx];
    if (chars < current.length) {
      const t = setTimeout(() => setChars(chars + 1), charDelay);
      return () => clearTimeout(t);
    }
    // 行終わり: 次の行へ or ループ
    if (lineIdx < lines.length - 1) {
      const t = setTimeout(() => { setLineIdx(lineIdx + 1); setChars(0); }, lineGap);
      return () => clearTimeout(t);
    }
    // 全行終わり → 一定時間置いてリセット
    const t = setTimeout(() => { setLineIdx(0); setChars(0); setVersion((v) => v + 1); }, restGap);
    return () => clearTimeout(t);
  }, [started, lineIdx, chars, lines, charDelay, lineGap, restGap]);

  // 既に表示が終わった行はフル表示
  const visibleLines = lines.slice(0, lineIdx + 1).map((line, i) => i < lineIdx ? line : line.slice(0, chars));
  const isLastTyping = chars < lines[lineIdx].length;

  return (
    <div ref={containerRef} className="space-y-1.5 min-h-[110px]" data-testid="line-typer" key={version}>
      {visibleLines.map((text, i) => {
        const isCurrent = i === lineIdx && isLastTyping;
        if (!text && !isCurrent) return null;
        return (
          <motion.div
            key={`${version}-${i}`}
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="inline-flex max-w-full bg-green-50 border border-green-100 rounded-2xl rounded-tl-sm px-3 py-1.5 text-sm text-stone-800 leading-relaxed"
            style={{ display: "flex" }}
          >
            <span>
              {text}
              {isCurrent && (
                <span className="inline-block w-0.5 h-4 bg-green-600 ml-0.5 align-middle animate-pulse" />
              )}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
}

// ============== 機能 ==============
function Features() {
  const items = [
    { icon: Smartphone, title: "スマホで希望提出", desc: "スタッフは専用画面からポチッと希望を送るだけ。集約不要。" },
    { icon: Wand2, title: "AIが自動仮配置", desc: "5秒で月の仮シフトが完成。「作り直し」「空き埋め」も選べる。" },
    { icon: Calendar, title: "日を選んでサクッと微調整", desc: "配置マジックで基本シフトはすでに入っている状態。あとは日付をタップして、足りないところ・代わってほしい人だけサッと調整するだけ。" },
    { icon: ShieldCheck, title: "ワンタップで公開", desc: "確定したら全員のスマホに通知。Excel配布の手間ともお別れ。" },
  ];
  return (
    <Section id="features" className="bg-[#FDFBF7]">
      <div className="text-center mb-12">
        <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
          だれでもカンタン、<br className="md:hidden" />4つのステップ。
        </motion.h2>
      </div>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((it, idx) => (
          <motion.div key={idx} variants={fadeUp}
            whileHover={{ y: -6, transition: { type: "spring", stiffness: 300 } }}
            className="bg-white rounded-3xl p-7 border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-shadow"
            data-testid={`feature-card-${idx}`}
          >
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-heading text-3xl font-black text-emerald-500/30">{idx + 1}</span>
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <it.icon className="w-4 h-4 text-emerald-700" />
              </div>
            </div>
            <h3 className="font-heading font-bold text-lg text-stone-800">{it.title}</h3>
            <p className="text-sm text-stone-500 mt-2 leading-relaxed">{it.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
}

// ============== お客様の声 ==============
function Voices() {
  const items = [
    {
      name: "居酒屋 ふくろう亭 / 店長", initial: "F", color: "bg-emerald-100 text-emerald-700",
      body: "シフト作成に毎月3時間かかっていたのが、20分で終わるように。空いた時間で売場改善に集中できています。",
    },
    {
      name: "デイサービス あさひ / 管理者", initial: "A", color: "bg-amber-100 text-amber-700",
      body: "スタッフからの希望集約がLINEバラバラだったのが一画面に。連勤回避や週上限も自動で考慮してくれて助かります。",
    },
    {
      name: "美容室 hana / オーナー", initial: "H", color: "bg-sky-100 text-sky-700",
      body: "やさまるのキャラクターに癒される、と若いスタッフからも好評です。アプリ全体が優しい印象で使いやすい。",
    },
  ];
  return (
    <Section id="voices" className="bg-white">
      <div className="text-center mb-12">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
          <Heart className="w-3.5 h-3.5" />お客様の声
        </motion.div>
        <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
          現場で、こんな声をいただいています。
        </motion.h2>
      </div>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
        className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((v, idx) => (
          <motion.div key={idx} variants={fadeUp}
            className="bg-[#FDFBF7] rounded-3xl p-7 border border-stone-100"
            data-testid={`voice-card-${idx}`}
          >
            <div className="flex gap-1 mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
            <p className="text-sm text-stone-700 leading-relaxed">「{v.body}」</p>
            <div className="flex items-center gap-3 mt-5 pt-5 border-t border-stone-100">
              <div className={`w-10 h-10 rounded-full ${v.color} flex items-center justify-center font-bold`}>{v.initial}</div>
              <p className="text-xs text-stone-500">{v.name}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
}

// ============== 料金 ==============
function Pricing() {
  return (
    <Section id="pricing" className="bg-gradient-to-b from-[#FDFBF7] to-[#FFF7E0]/40">
      <div className="text-center mb-10">
        <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
          やさしい料金プラン
        </motion.h2>
        <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.1 }} className="mt-3 text-stone-500 text-sm md:text-base">
          まずは2ヶ月、たっぷり無料で。
        </motion.p>
        <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.15 }} className="mt-2 inline-block bg-rose-50 text-rose-700 text-xs font-bold px-3 py-1 rounded-full border border-rose-200">
          ⏰ 2026年7月末までのお申込み限定
        </motion.p>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}
        className="relative max-w-2xl mx-auto rounded-[2rem] p-1 shadow-[0_20px_60px_-20px_rgba(16,185,129,0.25)]"
        style={{
          background: "linear-gradient(135deg, #34D399 0%, #FCD34D 50%, #34D399 100%)",
        }}
      >
        <div className="bg-white rounded-[1.85rem] p-8 md:p-12 text-center relative overflow-hidden">
          <motion.img src="/yasamaru-celebrate-128.png" alt="" className="absolute -top-4 right-4 w-20 md:w-24"
            animate={{ y: [0, -6, 0], rotate: [-3, 3, -3] }}
            transition={{ repeat: Infinity, duration: 3.6 }}
          />
          {/* バッジ: 期間限定 2ヶ月無料 */}
          <span className="inline-block bg-rose-100 text-rose-700 text-xs font-bold px-3 py-1 rounded-full mb-3">
            ✨ いまなら 2ヶ月 まるごと無料（7月末まで）
          </span>

          {/* 主役: 2ヶ月無料 (圧倒的に大きく) */}
          <p className="font-bold text-emerald-700 text-sm md:text-base mt-1">最初の2ヶ月のご利用料</p>
          <div className="mt-2 flex items-end justify-center gap-2">
            <span className="font-heading font-black text-emerald-600 leading-none tracking-tighter"
              style={{ fontSize: "clamp(80px, 14vw, 140px)" }}>
              ¥0
            </span>
            <div className="pb-3 md:pb-4 text-left">
              <p className="font-heading font-black text-emerald-600 text-3xl md:text-4xl leading-tight">
                2ヶ月
              </p>
              <p className="text-emerald-700 text-xs md:text-sm font-bold">完全無料</p>
            </div>
          </div>
          <p className="text-[11px] text-stone-400 mt-2">クレジットカード不要 / いつでも解約OK</p>

          {/* 3ヶ月目以降の月額 (脇役として控えめに) */}
          <div className="mt-5 max-w-sm mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-3 bg-stone-50 border border-stone-200/60 rounded-2xl px-3 py-3">
            <div>
              <p className="text-[10px] text-emerald-700 font-bold">トライアル中</p>
              <p className="font-heading text-xl font-black text-emerald-600">¥0 <span className="text-[10px] font-bold text-stone-500">/月</span></p>
            </div>
            <ArrowRight className="w-4 h-4 text-stone-400" />
            <div>
              <p className="text-[10px] text-stone-500 font-bold">3ヶ月目から</p>
              <p className="font-heading text-xl font-black text-stone-700">¥3,980<span className="text-[10px] text-stone-500 font-bold">/月（税抜）</span></p>
            </div>
          </div>

          <p className="text-stone-500 text-sm md:text-base mt-5 leading-relaxed max-w-md mx-auto">
            まずはたっぷり<b className="text-stone-700">2ヶ月、完全無料</b>でお試しください。<br />
            気に入っていただけたら、3ヶ月目から月額¥3,980で続けられます。
          </p>
          <p className="mt-3 text-[11px] text-rose-700 font-bold">
            ※ 2ヶ月無料は <b>2026年7月31日</b> までのお申込みが対象です。
          </p>

          <ul className="mt-6 space-y-2 text-sm text-stone-700 max-w-sm mx-auto text-left">
            {[
              "初期費用 ¥0、クレジットカード不要",
              "配置マジック含む全機能 使い放題",
              "スタッフ30名まで登録可能",
              "導入サポート・データ移行も無料",
              "いつでも解約OK、違約金なし",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{t}</li>
            ))}
          </ul>

          <p className="mt-5 text-[11px] text-stone-400 max-w-sm mx-auto">
            ※ スタッフ31名以上の店舗は、別途プランをご用意しています。<a href="#contact" className="text-emerald-700 font-bold underline underline-offset-2">ご相談ください</a>。
          </p>

          <a href="#contact" data-testid="pricing-cta"
            className="mt-8 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-3.5 rounded-full text-base shadow-[0_8px_24px_-6px_rgba(16,185,129,0.45)] transition-all hover:-translate-y-0.5">
            無料トライアルに申し込む<ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </motion.div>
    </Section>
  );
}

// ============== FAQ ==============
function FAQ() {
  const items = [
    { q: "導入にどれくらい時間がかかりますか？", a: "お申込み後、最短当日にアカウント発行。初期セットアップは15-30分程度で完了します。" },
    { q: "スタッフは何人まで登録できますか？", a: "スタンダードプラン(¥3,980/月)では1店舗あたりスタッフ30名まで登録できます。31名以上の場合は別途プランをご用意していますので、お気軽にご相談ください。" },
    { q: "既存のExcelシフトデータは移行できますか？", a: "はい。CSV/Excelからのインポートに対応しています。データ移行のサポートも無料で行います。" },
    { q: "スタッフ側にもアプリのインストールが必要ですか？", a: "ブラウザだけで利用できます。アプリのインストールは不要。スマホのホーム画面に追加も可能です。" },
    { q: "解約はいつでもできますか？", a: "はい。無料トライアル中・契約後ともに、いつでも解約可能です。違約金や最低契約期間はありません。" },
    { q: "個人情報の取り扱いは？", a: "シフトに必要な最小限の情報のみ取得し、暗号化通信(HTTPS)+データベース暗号化で安全に管理しています。" },
  ];
  return (
    <Section id="faq" className="bg-white">
      <div className="text-center mb-10">
        <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
          よくあるご質問
        </motion.h2>
      </div>
      <div className="max-w-2xl mx-auto space-y-3">
        {items.map((it, idx) => <FAQItem key={idx} idx={idx} {...it} />)}
      </div>
    </Section>
  );
}

function FAQItem({ idx, q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }}
      className="bg-[#FDFBF7] border border-stone-100 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(!open)} data-testid={`faq-${idx}`}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3">
        <span className="font-bold text-stone-800 text-sm md:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }} className="overflow-hidden">
            <p className="px-5 pb-4 text-sm text-stone-600 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============== 資料請求フォーム ==============
function ContactForm({ leadSource = "lp" }) {
  const [form, setForm] = useState({
    company: "", name: "", email: "", phone: "", store_count: "", industry: "", message: "", coupon_code: "", desired_login_id: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [couponState, setCouponState] = useState({ status: "idle", label: null }); // idle | valid | invalid
  const [loginIdState, setLoginIdState] = useState({ status: "idle", reason: null }); // idle | valid | invalid | taken
  const [consent, setConsent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // クーポンコードのリアルタイム検証 (デバウンス)
  useEffect(() => {
    const code = (form.coupon_code || "").trim();
    if (!code) { setCouponState({ status: "idle", label: null }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/leads/validate-coupon", { code });
        if (data.valid) setCouponState({ status: "valid", label: data.label });
        else setCouponState({ status: "invalid", label: null });
      } catch {
        setCouponState({ status: "invalid", label: null });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.coupon_code]);

  // 希望ログインIDのリアルタイム検証 (デバウンス)
  useEffect(() => {
    const id = (form.desired_login_id || "").trim();
    if (!id) { setLoginIdState({ status: "idle", reason: null }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/leads/check-login-id", { login_id: id });
        if (data.valid && data.available) setLoginIdState({ status: "valid", reason: null });
        else if (data.valid && !data.available) setLoginIdState({ status: "taken", reason: data.reason });
        else setLoginIdState({ status: "invalid", reason: data.reason });
      } catch {
        setLoginIdState({ status: "invalid", reason: "確認できませんでした" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.desired_login_id]);

  const [sentResult, setSentResult] = useState(null); // { auto_issued, email_sent, tenant_login_url }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      toast.error("会社名・お名前・メールアドレスは必須です");
      return;
    }
    // 希望ログインIDが入力されているのに無効/使用済の場合はブロック
    if (form.desired_login_id && (loginIdState.status === "invalid" || loginIdState.status === "taken")) {
      toast.error("希望ログインIDが利用できません。修正するか空欄にしてください。");
      return;
    }
    if (!consent) {
      toast.error("プライバシーポリシーと利用規約・免責事項への同意が必要です");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/leads", { ...form, source: leadSource });
      setSentResult(data || {});
      setSent(true);
      if (data?.auto_issued && data?.email_sent) {
        toast.success("ログイン情報をメールでお送りしました。受信箱をご確認ください。");
      } else if (data?.auto_issued) {
        toast.success("アカウントを発行しました。担当者よりご連絡します。");
      } else {
        toast.success("お申込みを受け付けました。担当者よりご連絡します。");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Section id="contact" className="bg-gradient-to-b from-[#FFF7E0]/40 to-[#FDFBF7]">
      <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
        <div>
          <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="font-heading font-black text-3xl md:text-4xl text-stone-800 tracking-tight">
            無料トライアル<br className="hidden md:inline" />お申込み
          </motion.h2>
          <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.1 }} className="mt-4 text-stone-600 leading-relaxed">
            2営業日以内に、担当者よりアカウント情報をご案内いたします。<br />
            まずは2ヶ月無料で、お気軽にお試しください。
          </motion.p>
          <ul className="mt-6 space-y-2 text-sm text-stone-600">
            {[
              "初期費用¥0・クレジットカード不要",
            ].map((t) => <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{t}</li>)}
          </ul>
          <img src="/yasamaru-celebrate-128.png" alt="" className="hidden lg:block mt-8 w-28 opacity-90" />
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-7 md:p-9 border border-stone-100 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.1)]"
        >
          {sent ? (
            <div className="text-center py-10" data-testid="lead-success">
              <img src="/yasamaru-celebrate-128.png" alt="" className="w-24 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-xl text-stone-800">ありがとうございます！</h3>
              {sentResult?.auto_issued && sentResult?.email_sent ? (
                <>
                  <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                    ログイン情報を <b className="text-emerald-700">{form.email}</b> 宛にお送りしました。<br />
                    そのまま<b>2ヶ月無料</b>でご利用いただけます。
                  </p>
                  {sentResult?.tenant_login_url && (
                    <p className="text-[11px] text-stone-400 mt-3 break-all">
                      メールが届かない場合は迷惑メールフォルダもご確認ください。
                    </p>
                  )}
                </>
              ) : sentResult?.auto_issued ? (
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  アカウントを発行しました。<br />
                  メール送信に問題があったため、担当者より <b className="text-emerald-700">{form.email}</b> 宛に直接ご連絡いたします。
                </p>
              ) : (
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  お申込みを受け付けました。<br />
                  2営業日以内に <b className="text-emerald-700">{form.email}</b> 宛にご案内します。
                </p>
              )}
              <button onClick={() => { setSent(false); setSentResult(null); setForm({ company: "", name: "", email: "", phone: "", store_count: "", industry: "", message: "", coupon_code: "", desired_login_id: "" }); setCouponState({ status: "idle", label: null }); setLoginIdState({ status: "idle", reason: null }); setConsent(false); }}
                className="mt-6 text-sm text-emerald-700 hover:text-emerald-800 font-bold underline underline-offset-2">
                別の問い合わせを送る
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
              <Field label="会社名・店舗名" required>
                <input type="text" required value={form.company} onChange={update("company")} data-testid="lead-company"
                  className="lp-input" placeholder="株式会社やさまる" />
              </Field>
              <Field label="お名前" required>
                <input type="text" required value={form.name} onChange={update("name")} data-testid="lead-name"
                  className="lp-input" placeholder="山田 太郎" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="メールアドレス" required>
                  <input type="email" required value={form.email} onChange={update("email")} data-testid="lead-email"
                    className="lp-input" placeholder="taro@example.com" />
                </Field>
                <Field label="電話番号">
                  <input type="tel" value={form.phone} onChange={update("phone")} data-testid="lead-phone"
                    className="lp-input" placeholder="03-1234-5678" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="店舗数">
                  <select value={form.store_count} onChange={update("store_count")} data-testid="lead-store-count" className="lp-input">
                    <option value="">選択してください</option>
                    <option>1店舗</option>
                    <option>2〜5店舗</option>
                    <option>6〜10店舗</option>
                    <option>11店舗以上</option>
                  </select>
                </Field>
                <Field label="業種">
                  <select value={form.industry} onChange={update("industry")} data-testid="lead-industry" className="lp-input">
                    <option value="">選択してください</option>
                    <option>飲食</option>
                    <option>小売</option>
                    <option>介護・福祉</option>
                    <option>美容・サロン</option>
                    <option>医療</option>
                    <option>その他</option>
                  </select>
                </Field>
              </div>
              <Field label="ご質問・ご要望(任意)">
                <textarea rows={3} value={form.message} onChange={update("message")} data-testid="lead-message"
                  className="lp-input resize-none" placeholder="例: 配送業の現場で使えるか知りたいです。" />
              </Field>
              <Field label="希望ログインID(任意)">
                <input
                  type="text"
                  value={form.desired_login_id}
                  onChange={(e) => setForm({ ...form, desired_login_id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                  data-testid="lead-login-id"
                  className="lp-input"
                  placeholder="例: yasamaru-shop (3〜20文字、英小文字・数字・- _)"
                  maxLength={20}
                  autoComplete="off"
                />
                <p className="mt-1 text-[11px] text-stone-500 leading-relaxed">
                  店長さまの企業ログインID。スタッフが店舗ログイン時に入力するID（=企業ID）にもなります。<br />
                  未入力の場合はメールアドレスから自動生成します。
                </p>
                {loginIdState.status === "valid" && (
                  <p className="mt-1.5 text-xs text-emerald-700 font-bold inline-flex items-center gap-1" data-testid="login-id-valid">
                    ✓ このIDは利用可能です
                  </p>
                )}
                {loginIdState.status === "taken" && (
                  <p className="mt-1.5 text-xs text-rose-600" data-testid="login-id-taken">
                    ✗ {loginIdState.reason || "既に使用されています"}
                  </p>
                )}
                {loginIdState.status === "invalid" && (
                  <p className="mt-1.5 text-xs text-rose-600" data-testid="login-id-invalid">
                    ✗ {loginIdState.reason || "形式が正しくありません"}
                  </p>
                )}
              </Field>
              <Field label="キャンペーンコード(任意)">
                <input type="text" value={form.coupon_code} onChange={update("coupon_code")} data-testid="lead-coupon"
                  className="lp-input" placeholder="お持ちの方のみ入力" autoComplete="off" />
                {couponState.status === "valid" && (
                  <p className="mt-1.5 text-xs text-emerald-700 font-bold inline-flex items-center gap-1" data-testid="coupon-valid">
                    🎟️ 適用OK：{couponState.label}
                  </p>
                )}
                {couponState.status === "invalid" && (
                  <p className="mt-1.5 text-xs text-rose-600" data-testid="coupon-invalid">
                    このコードは無効、もしくは期限切れです
                  </p>
                )}
              </Field>

              {/* 同意チェックボックス */}
              <label className="flex items-start gap-2.5 cursor-pointer select-none bg-stone-50 border border-stone-200 rounded-lg p-3" data-testid="consent-block">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="lead-consent"
                  className="mt-0.5 w-4 h-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500 shrink-0"
                />
                <span className="text-xs text-stone-700 leading-relaxed">
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-bold underline underline-offset-2 hover:text-emerald-800" data-testid="link-privacy">プライバシーポリシー</a>
                  および
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-bold underline underline-offset-2 hover:text-emerald-800" data-testid="link-terms">利用規約・免責事項</a>
                  に同意する<span className="text-rose-500 ml-0.5">*</span>
                </span>
              </label>

              <button type="submit" disabled={sending || !consent} data-testid="lead-submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 rounded-full text-base shadow-[0_8px_24px_-6px_rgba(16,185,129,0.45)] transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2">
                {sending ? "送信中…" : (<>無料トライアルに申し込む<ArrowRight className="w-4 h-4" /></>)}
              </button>
              <p className="text-[11px] text-stone-400 text-center leading-relaxed">送信いただいた情報は弊社プライバシーポリシーに基づき、本サービスのご案内のみに利用します。</p>
            </form>
          )}
        </motion.div>
      </div>
    </Section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-stone-700 mb-1.5 inline-flex items-center gap-1">
        {label}{required && <span className="text-rose-500">*</span>}
      </span>
      {children}
    </label>
  );
}

// ============== フッター ==============
function Footer() {
  return (
    <footer className="bg-stone-900 text-stone-300 pt-12 pb-8" data-testid="lp-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/yasamaru-celebrate-128.png" alt="" className="w-9 h-9" />
              <span className="font-heading font-bold text-white text-lg">やさまるシフト</span>
            </div>
            <p className="text-xs text-stone-400 leading-relaxed">やさしく、まるく、シフトにマジック。<br />シフト作成を、月末作業から月初の余裕へ。</p>
          </div>
          <div>
            <h4 className="text-white font-bold text-sm mb-3">プロダクト</h4>
            <ul className="space-y-2 text-xs">
              <li><a href="#features" className="hover:text-white">特長</a></li>
              <li><a href="#magic" className="hover:text-white">配置マジック</a></li>
              <li><a href="#pricing" className="hover:text-white">料金</a></li>
              <li><a href="#faq" className="hover:text-white">よくある質問</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold text-sm mb-3">サポート</h4>
            <ul className="space-y-2 text-xs">
              <li><a href="#contact" className="hover:text-white">お問い合わせ</a></li>
              <li><Link to="/login" className="hover:text-white">ご利用中の方はこちら</Link></li>
              <li><Link to="/privacy" className="hover:text-white">プライバシーポリシー</Link></li>
              <li><Link to="/terms" className="hover:text-white">利用規約・免責事項</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-stone-800 text-center text-[11px] text-stone-500">
          © {new Date().getFullYear()} やさまるシフト. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// ============== メイン ==============
export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const campaign = detectCampaign();

  // ログイン済みなら自動で本アプリへ
  useEffect(() => {
    if (loading) return;
    if (user) {
      if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "manager") navigate("/manager", { replace: true });
      else navigate("/staff", { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="lp-root min-h-screen bg-[#FDFBF7] text-stone-800" data-testid="landing-page">
      <Navbar />
      <Hero />
      {campaign && <MonitorCampaign campaign={campaign} />}
      <PainPoints />
      <MagicDemo />
      <Showcase />
      <ProxyAIFeature />
      <Features />
      <Voices />
      <Pricing />
      <FAQ />
      <ContactForm leadSource={campaign ? `lp:${campaign.slug}` : "lp"} />
      <Footer />
    </div>
  );
}
